import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { Layout } from '../components/Layout';
import { TransferOwnerModal } from '../components/TransferOwnerModal';
import { USER_ROLE_LABEL, USER_STATUS_LABEL } from '../constants/labels';
import { getErrorMessage } from '../types/api-error';
import type { ManagedUser } from '../types/admin';

export function UsersPage() {
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [mobile, setMobile] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [transferFrom, setTransferFrom] = useState<ManagedUser | null>(null);

  // 渲染层防御：确保 users 永远是数组，避免非数组值导致 .map 白屏
  const safeUsers = Array.isArray(users) ? users : [];

  const fetchUsers = async (q?: string) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get<ManagedUser[]>('/users', {
        params: q ? { mobile: q.trim() } : {},
      });
      console.log('[UsersPage] /users response data:', data, 'isArray:', Array.isArray(data));
      // 防御：确保是数组，兼容后端 {code,message,data} 包装解包后的各种可能形状
      const list: ManagedUser[] = Array.isArray(data) ? data : Array.isArray((data as any)?.items) ? (data as any).items : Array.isArray((data as any)?.list) ? (data as any).list : [];
      setUsers(list);
    } catch (err) {
      console.log('[UsersPage] /users error:', err);
      setError(getErrorMessage(err, '加载用户失败'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('[UsersPage] mounted, pathname:', window.location.pathname);
    fetchUsers();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchUsers(mobile);
  };



  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900">账号管理</h2>
      </div>

      <form onSubmit={handleSearch} className="flex gap-2 mb-4">
        <input
          type="text"
          inputMode="numeric"
          placeholder="搜索手机号"
          value={mobile}
          onChange={(e) => setMobile(e.target.value)}
          className="flex-1 min-w-0 px-4 py-2.5 rounded-lg border border-gray-300 bg-white text-base"
        />
        <button
          type="submit"
          className="px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 active:bg-indigo-800 whitespace-nowrap"
        >
          搜索
        </button>
        <button
          type="button"
          onClick={() => {
            setMobile('');
            fetchUsers();
          }}
          className="px-4 py-2.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 whitespace-nowrap"
        >
          重置
        </button>
      </form>

      {error && (
        <div className="mb-4 text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</div>
      )}

      {loading ? (
        <div className="text-center py-10 text-gray-500">加载中…</div>
      ) : safeUsers.length === 0 ? (
        <div className="text-center py-10 text-gray-500">未找到账号</div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden sm:block bg-white rounded-xl shadow overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">手机号</th>
                  <th className="px-4 py-3 text-left font-medium">昵称</th>
                  <th className="px-4 py-3 text-left font-medium">角色</th>
                  <th className="px-4 py-3 text-left font-medium">状态</th>
                  <th className="px-4 py-3 text-left font-medium">所属门店</th>
                  <th className="px-4 py-3 text-left font-medium">门店额度</th>
                  <th className="px-4 py-3 text-left font-medium">会员开始</th>
                  <th className="px-4 py-3 text-right font-medium">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {safeUsers.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">{u.mobile}</td>
                    <td className="px-4 py-3">{u.nickname || '-'}</td>
                    <td className="px-4 py-3">{USER_ROLE_LABEL[u.role]}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          u.status === 'active'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {USER_STATUS_LABEL[u.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3">{u.storeNames?.join(', ') || '-'}</td>
                    <td className="px-4 py-3">{u.storeCreationLimit}</td>
                    <td className="px-4 py-3">{u.premiumStartedAt || '-'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex flex-wrap justify-end gap-2">
                        {u.role === 'owner' && (
                          <button
                            type="button"
                            onClick={() => setTransferFrom(u)}
                            className="inline-flex items-center px-3 py-1.5 rounded-md bg-amber-50 text-amber-700 text-xs font-medium hover:bg-amber-100 active:bg-amber-200"
                          >
                            转移 owner
                          </button>
                        )}
                        <Link
                          to={`/users/${u.id}/edit`}
                          className="inline-flex items-center px-3 py-1.5 rounded-md bg-indigo-50 text-indigo-700 text-xs font-medium hover:bg-indigo-100 active:bg-indigo-200"
                        >
                          编辑
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="sm:hidden space-y-3">
            {safeUsers.map((u) => (
              <div key={u.id} className="bg-white rounded-xl shadow p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium text-gray-900">{u.mobile}</div>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      u.status === 'active'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {USER_STATUS_LABEL[u.status]}
                  </span>
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <div className="flex justify-between">
                    <span>昵称</span>
                    <span className="text-gray-900">{u.nickname || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>角色</span>
                    <span className="text-gray-900">{USER_ROLE_LABEL[u.role]}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>所属门店</span>
                    <span className="text-gray-900">{u.storeNames?.join(', ') || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>门店额度</span>
                    <span className="text-gray-900">{u.storeCreationLimit}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>会员开始</span>
                    <span className="text-gray-900">{u.premiumStartedAt || '-'}</span>
                  </div>
                </div>
                <div className="mt-3 flex flex-col gap-2">
                  {u.role === 'owner' && (
                    <button
                      type="button"
                      onClick={() => setTransferFrom(u)}
                      className="w-full text-center px-4 py-2.5 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 active:bg-amber-800"
                    >
                      转移 owner
                    </button>
                  )}
                  <Link
                    to={`/users/${u.id}/edit`}
                    className="w-full text-center px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 active:bg-indigo-800"
                  >
                    编辑
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {transferFrom && (
        <TransferOwnerModal
          fromUser={transferFrom}
          onClose={() => setTransferFrom(null)}
          onSuccess={() => {
            setTransferFrom(null);
            fetchUsers(mobile || undefined);
          }}
        />
      )}
    </Layout>
  );
}
